import { Button, formatDate as formatUiDate, Spinner, useTranslation } from '@hbs-us/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { useId, useState } from 'react';
import { apiFetchJson } from '../../lib/apiFetch.js';
import type {
  BillingResponse,
  BillingResyncResponse,
  Cadence,
  CheckoutEstimateResponse,
  PlanCode,
  PricingTier,
} from './contractsMirror.js';

/**
 * The workspace-billing page (27.2-05 — formerly the misnamed `BillingComingSoon`, which
 * already read the real endpoint).
 *
 * It renders `GET /api/v1/billing` and offers the three management actions an Admin needs:
 * subscription-mode checkout per self-serve tier (B2), the Stripe billing portal, and a
 * Resync that re-pulls the authoritative Stripe subscription
 * (`POST /api/v1/billing/resync`, 27.2-04).
 *
 * The tier cards are driven ENTIRELY by `pricing.tiers` — whatever the platform-admin
 * pricing config marks `checkout: true` is what renders, so a price change or a new tier
 * never needs a dashboard deploy. Enterprise (`checkout: false`) stays a contact link,
 * never a card with an invented price.
 *
 * Every management control lives inside the `canManage` branch (Admin-only per the billing
 * response). That is the UI half of T-27.2-05-01 — the server is the real gate: checkout,
 * portal, cancel/reactivate and resync all reject a non-Admin.
 */

function money(cents: number | null, currency = 'usd'): string {
  if (cents === null) return 'Contact QELM';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function date(value: string | null, locale: string): string {
  return value ? formatUiDate(value, locale, { dateStyle: 'medium' }) : 'Not set';
}

const CARD = 'rounded-lg border border-border bg-card p-5';

export function BillingPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const queryClient = useQueryClient();
  const planGroupName = useId();
  const query = useQuery({
    queryKey: ['billing'],
    queryFn: () => apiFetchJson<BillingResponse>('/api/v1/billing'),
  });
  const [plan, setPlan] = useState<Exclude<PlanCode, 'enterprise'> | null>(null);
  const [cadence, setCadence] = useState<Cadence | null>(null);
  const selectedPlan = plan ?? query.data?.subscription.plan ?? 'solo';
  const selectedCadence =
    cadence ?? query.data?.subscription.cadence ?? query.data?.pricing.defaultCadence ?? 'monthly';
  const estimate = useQuery({
    queryKey: ['billing-estimate', selectedPlan, selectedCadence],
    enabled: Boolean(query.data),
    queryFn: () =>
      apiFetchJson<CheckoutEstimateResponse>('/api/v1/billing/estimate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: selectedPlan, cadence: selectedCadence }),
      }),
  });
  const action = useMutation({
    mutationFn: async (input: {
      path: string;
      body?: Record<string, unknown>;
      redirect?: boolean;
    }) => {
      const result = await apiFetchJson<{ ok: true; url?: string }>(input.path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input.body ?? {}),
      });
      if (input.redirect && result.url) window.location.assign(result.url);
      return result;
    },
    onSuccess: (_result, input) => {
      if (!input.redirect) void queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
  // Resync is its own mutation rather than another `action` path so it can report its own
  // pending/success/error state without the checkout and portal buttons sharing it.
  const resync = useMutation({
    mutationFn: () =>
      apiFetchJson<BillingResyncResponse>('/api/v1/billing/resync', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing'] }),
  });

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <section role="alert" className="mx-auto max-w-2xl p-8 text-center">
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('nav:billing.unavailable')}
        </h1>
        <Button className="mt-4" onClick={() => void query.refetch()}>
          {t('common:error.retry')}
        </Button>
      </section>
    );
  }

  const { pricing, subscription, invoices, canManage } = query.data;
  const selectedTier = pricing.tiers.find((tier) => tier.code === selectedPlan);
  const selfServeTiers = pricing.tiers.filter((tier) => tier.checkout);
  const annual = selectedCadence === 'annual';
  const overageMultiplier = annual ? pricing.annualMonthsCharged : 1;
  const canCheckout =
    !subscription.stripeSubscriptionPresent ||
    ['canceled', 'unpaid', 'paused'].includes(String(subscription.rawStatus));

  /** What a tier bills at the selected cadence (annual charges N months up front). */
  function tierAmount(tier: PricingTier): number | null {
    if (tier.monthlyCents === null) return null;
    return annual ? tier.monthlyCents * pricing.annualMonthsCharged : tier.monthlyCents;
  }

  function startCheckout(code: Exclude<PlanCode, 'enterprise'>) {
    setPlan(code);
    action.mutate({
      path: '/api/v1/billing/checkout',
      body: { plan: code, cadence: selectedCadence },
      redirect: true,
    });
  }

  return (
    <main className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-6 lg:p-10">
      <header className="max-w-2xl">
        <p className="font-semibold text-primary-ink text-xs uppercase tracking-[0.08em]">
          {t('nav:billing.eyebrow')}
        </p>
        <h1 className="mt-2 font-display font-semibold text-3xl text-foreground">
          {t('nav:billing.heading')}
        </h1>
        <p className="mt-2 text-muted-foreground">{t('nav:billing.body')}</p>
      </header>

      <section aria-label={t('nav:billing.statusHeading')}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-semibold text-foreground text-xl">
            {t('nav:billing.statusHeading')}
          </h2>
          {canManage ? (
            <div className="flex flex-wrap items-center gap-3">
              {resync.isSuccess ? (
                <p className="text-muted-foreground text-sm">{t('nav:billing.resyncSuccess')}</p>
              ) : null}
              {resync.isError ? (
                <p role="alert" className="text-destructive text-sm">
                  {t('nav:billing.resyncFailed')}
                </p>
              ) : null}
              <Button
                type="button"
                variant="outline"
                onClick={() => resync.mutate()}
                loading={resync.isPending}
              >
                {t('nav:billing.resync')}
              </Button>
            </div>
          ) : null}
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <div className={CARD}>
            <p className="text-muted-foreground text-sm">{t('nav:billing.status')}</p>
            <p className="mt-2 font-semibold text-card-foreground capitalize">
              {subscription.state.replaceAll('_', ' ')}
            </p>
          </div>
          <div className={CARD}>
            <p className="text-muted-foreground text-sm">{t('nav:billing.seats')}</p>
            <p className="mt-2 font-semibold text-card-foreground">
              {subscription.seats.office} {t('nav:billing.office')} · {subscription.seats.field}{' '}
              {t('nav:billing.field')}
            </p>
          </div>
          <div className={CARD}>
            <p className="text-muted-foreground text-sm">{t('nav:billing.nextDate')}</p>
            <p className="mt-2 font-semibold text-card-foreground">
              {date(
                subscription.graceUntil ??
                  subscription.recoveryUntil ??
                  subscription.trialEndsAt ??
                  subscription.currentPeriodEnd,
                locale,
              )}
            </p>
          </div>
        </div>
      </section>

      {canManage ? (
        <section className={CARD}>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-xl">
              <h2 className="font-semibold text-card-foreground text-xl">
                {t('nav:billing.plansHeading')}
              </h2>
              <p className="mt-1 text-muted-foreground text-sm">{t('nav:billing.plansBody')}</p>
            </div>
            <label className="flex flex-col gap-2 text-sm">
              <span className="font-medium text-foreground">{t('nav:billing.cadence')}</span>
              <select
                className="h-11 rounded-md border border-input bg-background px-3"
                value={selectedCadence}
                onChange={(event) => setCadence(event.target.value as Cadence)}
              >
                <option value="monthly">{t('nav:billing.monthly')}</option>
                <option value="annual">{t('nav:billing.annual')}</option>
              </select>
            </label>
          </div>

          {/*
            One grid, one shared row per role: each card carries the name + price header, a
            fixed-height seats line, the feature list, then a bottom-anchored footer of the
            SAME height whether it holds a CTA or the current-plan marker. Nothing here
            depends on how long one tier's feature list happens to run.
          */}
          <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {selfServeTiers.map((tier) => {
              const code = tier.code as Exclude<PlanCode, 'enterprise'>;
              const isSelected = code === selectedPlan;
              const isCurrent = code === subscription.plan;
              return (
                <div
                  key={tier.code}
                  className={`flex h-full flex-col rounded-md border bg-background p-4 ${
                    isSelected ? 'border-primary' : 'border-border'
                  }`}
                >
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="radio"
                      name={planGroupName}
                      value={code}
                      checked={isSelected}
                      onChange={() => setPlan(code)}
                      className="mt-1.5 size-4 shrink-0 accent-primary"
                    />
                    <span className="min-w-0">
                      <span className="block font-display font-semibold text-foreground">
                        {tier.name}
                      </span>
                      <span className="mt-1 block font-semibold text-2xl text-foreground tabular-nums">
                        {money(tierAmount(tier), pricing.currency)}
                      </span>
                      <span className="block text-muted-foreground text-sm">
                        {annual ? t('nav:billing.perYear') : t('nav:billing.perMonth')}
                      </span>
                    </span>
                  </label>

                  <p className="mt-4 min-h-10 text-muted-foreground text-sm">
                    {tier.includedSeats
                      ? `${t('nav:billing.includedSeats')}: ${tier.includedSeats.office} ${t('nav:billing.office')} · ${tier.includedSeats.field} ${t('nav:billing.field')} · ${tier.includedSeats.finance} ${t('nav:billing.finance')}`
                      : t('nav:billing.contact')}
                  </p>

                  <ul className="mt-3 flex flex-col gap-1.5 text-foreground text-sm">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <Check
                          className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                          aria-hidden="true"
                        />
                        <span>{t(`nav:billing.feature.${feature}`)}</span>
                      </li>
                    ))}
                  </ul>

                  {/*
                    The footer reserves exactly one button's height in every card, whether
                    it holds a CTA, the current-plan marker, or nothing, so the buttons sit
                    on one line across the row.
                  */}
                  <div className="mt-auto pt-8">
                    <div className="flex h-11 items-center">
                      {isCurrent ? (
                        <p className="font-medium text-muted-foreground text-sm">
                          {t('nav:billing.currentPlan')}
                        </p>
                      ) : canCheckout ? (
                        <Button
                          type="button"
                          className="w-full"
                          onClick={() => startCheckout(code)}
                          disabled={action.isPending}
                        >
                          {t('nav:billing.choosePlan', { plan: tier.name })}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 grid gap-3 rounded-md border border-border bg-background p-4 text-sm sm:grid-cols-2">
            <p>
              <span className="text-muted-foreground">{t('nav:billing.plan')}:</span>{' '}
              {selectedTier?.name ?? selectedPlan}
            </p>
            <p>
              <span className="text-muted-foreground">{t('nav:billing.currentSeats')}:</span>{' '}
              {subscription.seats.office} {t('nav:billing.office')} · {subscription.seats.field}{' '}
              {t('nav:billing.field')}
            </p>
            <p>
              <span className="text-muted-foreground">{t('nav:billing.overages')}:</span>{' '}
              {estimate.data
                ? `${estimate.data.estimate.overages.office} × ${money(pricing.seatOverageMonthlyCents.office * overageMultiplier)} ${t('nav:billing.office')} · ${estimate.data.estimate.overages.field} × ${money(pricing.seatOverageMonthlyCents.field * overageMultiplier)} ${t('nav:billing.field')} · ${estimate.data.estimate.overages.dispatcher} × ${money(pricing.seatOverageMonthlyCents.dispatcher * overageMultiplier)} ${t('nav:billing.dispatcher')}`
                : t('common:status.loading')}
            </p>
            <p>
              <span className="text-muted-foreground">{t('nav:billing.estimatedTotal')}:</span>{' '}
              <strong>
                {estimate.data
                  ? `${money(estimate.data.estimate.totalCents, pricing.currency)} / ${estimate.data.estimate.interval}`
                  : t('common:status.loading')}
              </strong>
            </p>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            {subscription.stripeCustomerPresent ? (
              <Button
                variant="secondary"
                onClick={() => action.mutate({ path: '/api/v1/billing/portal', redirect: true })}
                disabled={action.isPending}
              >
                {t('nav:billing.portal')}
              </Button>
            ) : null}
            {subscription.stripeSubscriptionPresent ? (
              <Button
                variant="secondary"
                onClick={() =>
                  action.mutate({
                    path: subscription.cancelAtPeriodEnd
                      ? '/api/v1/billing/reactivate'
                      : '/api/v1/billing/cancel',
                  })
                }
                disabled={action.isPending}
              >
                {subscription.cancelAtPeriodEnd
                  ? t('nav:billing.reactivate')
                  : t('nav:billing.cancel')}
              </Button>
            ) : null}
            <a
              href="/support"
              className="inline-flex h-11 items-center px-2 font-medium text-primary-ink text-sm underline decoration-1 underline-offset-2"
            >
              {t('nav:billing.enterprise')}
            </a>
          </div>
          {action.isError ? (
            <p role="alert" className="mt-3 text-destructive text-sm">
              {t('nav:billing.actionFailed')}
            </p>
          ) : null}
        </section>
      ) : (
        <p className={`${CARD} text-muted-foreground`}>{t('nav:billing.adminOnly')}</p>
      )}

      <section className={CARD}>
        <h2 className="font-semibold text-card-foreground text-xl">{t('nav:billing.invoices')}</h2>
        {invoices.length ? (
          <ul className="mt-4 divide-y divide-border">
            {invoices.map((invoice) => (
              <li
                key={invoice.id}
                className="flex flex-wrap items-center justify-between gap-3 py-3"
              >
                <div>
                  <p className="font-medium text-foreground">{invoice.number ?? invoice.id}</p>
                  <p className="text-muted-foreground text-sm">
                    {money(invoice.amountPaidCents, invoice.currency)} ·{' '}
                    {date(invoice.createdAt, locale)}
                  </p>
                </div>
                {invoice.hostedInvoiceUrl ? (
                  <a
                    href={invoice.hostedInvoiceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-primary-ink text-sm underline decoration-1 underline-offset-2"
                  >
                    {t('nav:billing.viewInvoice')}
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-muted-foreground text-sm">{t('nav:billing.noInvoices')}</p>
        )}
      </section>
    </main>
  );
}
